import { useCallback, useEffect, useState } from 'react';
import {
  getCaptureDraftStore,
  isRecoverableCaptureDraft,
  type CaptureDraft,
} from '../../data/drafts/capture-draft';
import {
  getCaptureAudioStore,
  matchCaptureAudio,
  type CaptureAudioMatch,
} from '../../data/drafts/capture-audio';
import { reportError } from '../../lib/monitoring';

/**
 * Reading the one recoverable Capture draft, for the surface that OFFERS it.
 *
 * M4 / S-04-01, under D-019 (J-3): recovery is offered, never taken. This hook
 * reads and nothing else — it does not navigate, does not restore, and above
 * all does not clear. A draft survives being looked at.
 *
 * `checking` is a real state and is kept distinct from `none` for the same
 * reason `SessionStatus` keeps them apart: "there is no unfinished Capture"
 * and "we have not looked yet" are different facts, and collapsing them makes
 * a recovery card flash into existence a beat after the screen settles.
 */
export type RecoverableCaptureDraftState =
  | { status: 'checking' }
  | { status: 'none' }
  // `readAtMs` is stamped when the read resolves. The card's "how old" line
  // needs a clock, the clock cannot be read during render, and an age fixed at
  // read time does not drift while Brandon is looking at it.
  | { status: 'found'; draft: CaptureDraft; readAtMs: number; audio: CaptureAudioMatch };

export interface RecoverableCaptureDraft {
  state: RecoverableCaptureDraftState;
  /**
   * Brandon's explicit Discard — one of the three terminal outcomes that may
   * remove a draft (D-019). Nothing else in this hook deletes anything.
   */
  discard: () => Promise<void>;
}

/**
 * `ownerUserId` is `null` whenever the signed-in identity is not known — no
 * session, still checking, or no session context at all. In every one of those
 * cases the answer is `none`, and NO READ IS ISSUED: a draft is never looked
 * up, let alone rendered, without an authenticated owner to match it against.
 * That is what keeps a draft off the sign-in screen and away from a second
 * user on the same device.
 */
export function useRecoverableCaptureDraft(ownerUserId: string | null): RecoverableCaptureDraft {
  /**
   * The answer is stored WITH the identity it answered for, and the state
   * below is derived from the two. A stale answer therefore becomes `checking`
   * by arithmetic — no effect has to reset it, so no effect calls `setState`
   * synchronously, and no answer for User A can ever be rendered while User B
   * is signed in.
   */
  const [lookup, setLookup] = useState<{
    owner: string;
    state: RecoverableCaptureDraftState;
  } | null>(null);

  const state: RecoverableCaptureDraftState =
    ownerUserId === null
      ? { status: 'none' }
      : lookup?.owner === ownerUserId
        ? lookup.state
        : { status: 'checking' };

  useEffect(() => {
    if (ownerUserId === null) return undefined;

    let cancelled = false;
    const read = async (): Promise<RecoverableCaptureDraftState> => {
      const draft = await getCaptureDraftStore().read(ownerUserId);
      if (!draft || !isRecoverableCaptureDraft(draft)) return { status: 'none' };
      // READ-ONLY, deliberately. The card says whether a recording is waiting;
      // it never resolves, deletes or cleans anything up. Orphan sweeping and
      // dead-pointer clearing belong to the Capture screen, which is where
      // Brandon can actually be told what happened.
      const record = await getCaptureAudioStore().read(ownerUserId);
      return {
        status: 'found',
        draft,
        readAtMs: Date.now(),
        audio: matchCaptureAudio(draft, record, Date.now()),
      };
    };

    void read()
      .then((state) => {
        if (cancelled) return;
        setLookup({ owner: ownerUserId, state });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // An unreadable store means no offer — never a crash, and never a
        // deletion. The draft, if there is one, is still on disk for the next
        // launch to find.
        reportError(error, 'capture.draft.read');
        setLookup({ owner: ownerUserId, state: { status: 'none' } });
      });

    return () => {
      cancelled = true;
    };
  }, [ownerUserId]);

  const discard = useCallback(async () => {
    if (ownerUserId === null) return;
    try {
      await getCaptureDraftStore().clear(ownerUserId);
    } catch (error) {
      reportError(error, 'capture.draft.clear');
    }
    // Discarding the unfinished Capture discards its recording too (S-04-02).
    // Cleared separately so a failure removing one cannot skip the other, and
    // the card is dismissed either way — he asked for it to be gone.
    try {
      await getCaptureAudioStore().clear(ownerUserId);
    } catch (error) {
      reportError(error, 'capture.audio.clear');
    }
    setLookup({ owner: ownerUserId, state: { status: 'none' } });
  }, [ownerUserId]);

  return { state, discard };
}
